/**
 * useLiftDisplayPreference — app-wide resolver for the per-DC lift display
 * preference (CUSTOMER vs REHRIG).
 *
 * The Lift Mapping tab (Settings/Alerts) lets an admin choose, per Distribution
 * Center, whether lifts are labelled by the customer alias or the Rehrig id
 * (stored on DUO_DIOT_DC_SETTINGS.LIFT_DISPLAY_PREFERENCE). This preference is
 * ENFORCED across every Delivery Insights grid / detail view that shows a lift,
 * NOT just inside the Lift Mapping tab:
 *
 *   - REHRIG   -> show ONLY the Rehrig lift id everywhere.
 *   - CUSTOMER -> show the customer alias IN PLACE OF the Rehrig id (falling back
 *                to the Rehrig id when a lift has no alias).
 *
 * The ONE exception is the Lifts tab grid (LiftDataTable), which always shows
 * BOTH the Rehrig id and the alias regardless of this preference.
 *
 * SOURCE OF THE PREFERENCE (no admin-gated call): the per-DC preference is
 * surfaced ON the customer/locations tree that every DIoT page already loads for
 * all users (`/v2/admin/locations` -> `customerAndLocationsData`, each location
 * carries `liftDisplayPreference`). So this hook simply reads it from redux for
 * the currently-selected DC — no extra request, and it works for non-admin
 * viewers who cannot call the admin-only display-preference endpoint. Any
 * missing value degrades to the REHRIG default.
 *
 * @module useLiftDisplayPreference
 */

import { useSelector } from "react-redux";
import {
  DEFAULT_LIFT_PREFERENCE,
  LIFT_PREF_REHRIG,
  normalizeLiftPreference,
  computeLiftLabel,
} from "./liftDisplayPreferenceCache";

export {
  LIFT_PREF_CUSTOMER,
  LIFT_PREF_REHRIG,
  DEFAULT_LIFT_PREFERENCE,
  normalizeLiftPreference,
  computeLiftLabel,
  // Back-compat alias: the settings tab clears the process cache after a
  // preference change. The pure cache now lives in liftDisplayPreferenceCache.
  clearLiftPreferenceCache as _clearLiftPreferenceCache,
  clearLiftPreferenceCache,
} from "./liftDisplayPreferenceCache";

/** Resolve the currently-selected DC location id from redux + localStorage. */
export function selectSelectedLocationId(state) {
  const fromObj = state?.Routes?.selectedLocationListData?.locationId;
  if (fromObj != null && fromObj !== "") return fromObj;
  const fromId = state?.Routes?.selectedLocation;
  if (fromId != null && fromId !== "") return fromId;
  try {
    const stored = localStorage.getItem("routeDBLocation");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.locationId != null) return parsed.locationId;
    }
  } catch (e) {
    /* ignore malformed localStorage */
  }
  return null;
}

/**
 * Find a location object (across the customer tree) by its locationId, so we can
 * read the `liftDisplayPreference` the /v2/admin/locations layer attached to it.
 */
export function findLocationPreference(customerAndLocationsData, locationId) {
  if (!Array.isArray(customerAndLocationsData) || locationId == null) {
    return undefined;
  }
  for (const customer of customerAndLocationsData) {
    const locations = customer?.locations || [];
    const match = locations.find(
      (l) => String(l.locationId) === String(locationId)
    );
    if (match) return match.liftDisplayPreference;
  }
  return undefined;
}

/**
 * React hook: resolve the lift display preference for the currently-selected DC
 * (or an explicit one) by reading it from the redux customer/locations tree.
 *
 * @param {number|string} [explicitLocationId] - override the redux selection
 *   (e.g. a page that already has a specific DC in props).
 * @returns {{ preference: string, showCustomerLift: boolean,
 *             labelFor: (row:object)=>string, loading: boolean }}
 */
export default function useLiftDisplayPreference(explicitLocationId) {
  const selectedLocationId = useSelector(selectSelectedLocationId);
  const customerAndLocationsData = useSelector(
    (state) => state?.Routes?.customerAndLocationsData
  );
  const loading = useSelector(
    (state) => Boolean(state?.Routes?.customerAndLocationsDataLoad)
  );

  const locationId =
    explicitLocationId != null && explicitLocationId !== ""
      ? explicitLocationId
      : selectedLocationId;

  const raw = findLocationPreference(customerAndLocationsData, locationId);
  const preference = normalizeLiftPreference(raw);

  return {
    preference,
    showCustomerLift: preference !== LIFT_PREF_REHRIG,
    labelFor: (row) => computeLiftLabel(row, preference),
    loading,
  };
}
